"use client";

import { ServerContext } from "@/context/Server";
import useFirebaseStore from "@/store/firebase";
import { REPLICACHE_DB_NAME } from "@/utils/constants";
import defaultPreferences from "@/utils/defaultPreferences";
import demoSongs from "@/utils/demoSongs";
import type {
  FriendIdMap,
  Friends,
  MapMeta,
  SetList,
  SetLists,
  Song,
  SongId,
  SongIdMap,
  Songs,
  Timestamp,
  User,
  UserId,
  UserPreferences,
} from "@/utils/types";
import type { User as FirebaseUser } from "firebase/auth";
import { isEmpty, isEqual } from "lodash";
import { useCallback, useContext, useEffect } from "react";
import {
  type PullerResult,
  type ReadTransaction,
  Replicache,
  type WriteTransaction,
  getDefaultPuller,
} from "replicache";
import create from "zustand";
import { devtools } from "zustand/middleware";
import shallow from "zustand/shallow";
import useEventListener from "../hooks/useEventListener";

const LOG_TAG = "🤖 Replicache";
const DEBUG = false;

const IS_DEV = process.env.NODE_ENV === "development";
const PULL_INTERVAL = (IS_DEV ? 120 : 30) * 1000;
const PULL_URL = "/api/replicache/pull";
const PUSH_URL = "/api/replicache/push";
const getPullInterval = (): number | null => {
  let pullInterval: number | null = PULL_INTERVAL;
  if (document.hidden) pullInterval = null;
  if (DEBUG)
    console.debug(LOG_TAG, "getPullInterval", {
      hidden: document.hidden,
      pullInterval,
    });
  return pullInterval;
};

const setPullInterval = (
  replicache: Replicache,
  pullInterval: number | null,
  pullImmediately: boolean,
) => {
  if (DEBUG)
    console.debug(LOG_TAG, "setPullInterval", {
      pullInterval,
      hidden: document.hidden,
      pullImmediately,
    });
  if (pullInterval) {
    replicache.puller = getDefaultPuller(replicache);
    replicache.pullInterval = pullInterval;
    if (pullImmediately) replicache.pull();
  } else {
    replicache.puller = async ({ cookie }): Promise<PullerResult> => {
      return {
        httpRequestInfo: { errorMessage: "", httpStatusCode: 200 },
        /* @ts-ignore */
        response: { cookie, lastMutationIDChanges: {}, patch: [] },
      };
    };
    replicache.pullInterval = null;
  }
};

export type ReplicacheContextProps = {
  replicache: Replicache;
  setReplicache: (T: Replicache) => void;
  loading: boolean;
  setLoading: (T: boolean) => void;
  isFirstTimeUser: boolean;
  setIsFirstTimeUser: (T: boolean) => void;

  preferences: UserPreferences;
  initializedPreferences: boolean;
  setPreferences: (T: UserPreferences) => void;

  songIdMap: SongIdMap;
  initializedSongIdMap: boolean;

  songs: Songs;
  initializedSongs: boolean;

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  songIdMapModify: (args: any) => void;
  songAdd: (T: { song: Song; ts: Timestamp }) => Promise<void>;
  songModify: (T: Song) => Promise<void>;
  songRemove: (T: { song: Song; ts: Timestamp }) => Promise<void>;
  songViewCountIncrement: (T: SongId) => void;

  setLists: SetLists;
  setListAdd: (T: { setList: SetList; ts: Timestamp }) => Promise<void>;
  setListModify: (T: { setList: SetList; ts: Timestamp }) => Promise<void>;
  setListRemove: (T: { setList: SetList; ts: Timestamp }) => Promise<void>;

  friendIdMap: FriendIdMap;
  initializedFriendIdMap: boolean;

  friends: Friends;
  initializedFriends: boolean;
  friendAdd: (T: { friend: User; ts: Timestamp }) => Promise<void>;
  friendRemove: (T: { friendId: UserId; ts: Timestamp }) => Promise<void>;
};

const replicacheNotAvailable = async () =>
  console.error(LOG_TAG, "replicacheNotAvailable");

const useReplicacheStore = create<ReplicacheContextProps>(
  devtools(
    // @ts-ignore
    (set, get) => {
      return {
        replicache: undefined,
        setReplicache: (_replicache: Replicache) => {
          // console.debug(LOG_TAG, "useReplicacheStore setReplicache", {
          //   replicache: _replicache,
          // });
          set({ replicache: _replicache });
        },
        loading: false,
        setLoading: (_loading: boolean) => set({ loading: _loading }),
        isFirstTimeUser: false,
        setIsFirstTimeUser: (_isFirstTimeUser: boolean) =>
          set({ isFirstTimeUser: _isFirstTimeUser }),

        preferences: defaultPreferences,
        initializedPreferences: false,

        friends: {},
        initializedFriends: false,

        friendIdMap: {},
        initializedFriendIdMap: false,

        songs: {},
        songIdMap: {},
        initializedSongIdMap: false,

        setLists: {},

        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        setPreferences: (...args: any) => {
          //console.debug(LOG_TAG, "replicacheSetPreferences", { args });
          const replicache = get()?.replicache;
          return (
            replicache
              ? // @ts-ignore
                replicache.mutate["user-preferences"]
              : replicacheNotAvailable
          )(...args);
        },
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        songIdMapModify: (...args: any) => {
          const replicache = get()?.replicache;
          return (
            replicache
              ? // @ts-ignore
                replicache.mutate["user-songs-map-modify"]
              : replicacheNotAvailable
          )(...args);
        },
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        songModify: (...args: any) => {
          const replicache = get()?.replicache;
          return (
            replicache
              ? // @ts-ignore
                replicache.mutate["song-modify"]
              : replicacheNotAvailable
          )(...args);
        },
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        songAdd: (...args: any) => {
          const replicache = get()?.replicache;
          if (DEBUG) console.debug(LOG_TAG, "songAdd", { args, replicache });
          return (
            replicache
              ? // @ts-ignore
                replicache.mutate["song-add"]
              : replicacheNotAvailable
          )(...args);
        },
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        songRemove: (...args: any) => {
          const replicache = get()?.replicache;
          return (
            replicache
              ? // @ts-ignore
                replicache.mutate["song-remove"]
              : replicacheNotAvailable
          )(...args);
        },
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        setListAdd: (...args: any) => {
          const replicache = get()?.replicache;
          return (
            replicache
              ? // @ts-ignore
                replicache.mutate["set-list-add"]
              : replicacheNotAvailable
          )(...args);
        },
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        setListModify: (...args: any) => {
          const replicache = get()?.replicache;
          return (
            replicache
              ? // @ts-ignore
                replicache.mutate["set-list-modify"]
              : replicacheNotAvailable
          )(...args);
        },
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        setListRemove: (...args: any) => {
          const replicache = get()?.replicache;
          return (
            replicache
              ? // @ts-ignore
                replicache.mutate["set-list-remove"]
              : replicacheNotAvailable
          )(...args);
        },

        songViewCountIncrement: (songId: SongId) => {
          const { songIdMap, songIdMapModify } = get();
          if (DEBUG)
            console.debug(LOG_TAG, "songViewCountIncrement", {
              songId,
              song: songIdMap[songId],
            });
          const updates = {
            view_count: songIdMap[songId].view_count + 1,
            last_viewed_timestamp: Date.now(),
          };
          songIdMapModify({ songId, updates });
        },
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        friendAdd: (...args: any) => {
          const replicache = get()?.replicache;
          return (
            replicache
              ? // @ts-ignore
                replicache.mutate["friend-add"]
              : replicacheNotAvailable
          )(...args);
        },
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        friendRemove: (...args: any) => {
          const replicache = get()?.replicache;
          return (
            replicache
              ? // @ts-ignore
                replicache.mutate["friend-remove"]
              : replicacheNotAvailable
          )(...args);
        },
      };
    },
    { name: "useReplicacheStore" },
  ),
);

const useReplicache = () => {
  const user = useFirebaseStore(({ user }) => user, shallow);
  const { replicache, setReplicache } = useReplicacheStore(
    (state) => ({
      replicache: state.replicache,
      setReplicache: state.setReplicache,
    }),
    shallow,
  );
  if (DEBUG) console.debug(LOG_TAG, "useReplicache", { replicache });

  const initializeReplicacheAuth = useCallback(
    async (replicache: Replicache, firebaseUser: FirebaseUser) => {
      replicache.getAuth = async () => {
        const freshToken = await firebaseUser.getIdToken(true);
        if (DEBUG)
          console.debug(LOG_TAG, "replicache.getAuth re-call'd", {
            freshToken,
          });
        return freshToken;
      };
      const idToken = await firebaseUser.getIdToken(true);
      replicache.auth = idToken;
      replicache.pullURL = PULL_URL;
      replicache.pushURL = PUSH_URL;
      const newPullInterval = getPullInterval();
      setPullInterval(replicache, newPullInterval, true);
      if (DEBUG)
        console.debug(
          LOG_TAG,
          "initializing user.getIdToken/getAuth/pullInterval",
          {
            newPullInterval,
            idToken,
          },
        );
    },
    [],
  );

  useEffect(() => {
    if (!replicache) return;
    if (!user) return;
    if (replicache.pullURL) return;
    initializeReplicacheAuth(replicache, user);
  }, [replicache, user, initializeReplicacheAuth]);

  const setupReplicache = useCallback(async () => {
    const replicache = new Replicache({
      licenseKey: "lb656a95d4b10454ea19ea0cd53849e8e",
      name: REPLICACHE_DB_NAME,
      pullInterval: null,
      mutators: {
        "user-preferences": async (
          tx: WriteTransaction,
          p: UserPreferences,
        ) => {
          if (DEBUG)
            console.debug(LOG_TAG, "mutator setPreferences", {
              p,
              pushURL: replicache.pushURL,
              auth: replicache.auth,
            });
          await tx.put("user-preferences", p);
        },
        "user-songs-map-modify": async (
          tx: WriteTransaction,
          args: { songId: string; updates: MapMeta },
        ) => {
          const { songId, updates } = args;
          //console.debug( LOG_TAG, "mutator songIdMapModify", {songId, updates} );
          const songIdMapPrev =
            ((await tx.get("user-songs-map")) as SongIdMap) ?? {};
          const songIdMap = {
            ...songIdMapPrev,
            [songId]: {
              ...songIdMapPrev[songId],
              ...updates,
            },
          };
          await tx.put("user-songs-map", songIdMap);
        },
        "song-add": async (
          tx: WriteTransaction,
          { song, ts }: { song: Song; ts: Timestamp },
        ) => {
          const songIdMapPrev =
            ((await tx.get("user-songs-map")) as SongIdMap) || {};
          const songIdMap = {
            ...songIdMapPrev,
            [song.id]: {
              added_timestamp: ts,
              deleted_timestamp: 0,
              updated_timestamp: ts,
              view_count: 0,
              last_viewed_timestamp: ts,
            },
          };
          if (DEBUG)
            console.debug(LOG_TAG, "mutator replicacheSongAdd", {
              song,
              songIdMap,
            });
          await tx.put("user-songs-map", songIdMap);
          await tx.put(`song/${song.id}`, song);
        },
        "song-remove": async (
          tx: WriteTransaction,
          { song, ts }: { song: Song; ts: Timestamp },
        ) => {
          if (DEBUG) console.debug(LOG_TAG, "mutator songRemove", song);
          await tx.del(`song/${song.id}`);
          const songIdMapPrev =
            ((await tx.get("user-songs-map")) as SongIdMap) || {};
          const songIdMap = {
            ...songIdMapPrev,
            [song.id]: {
              ...songIdMapPrev[song.id],
              deleted_timestamp: ts,
              updated_timestamp: ts,
            },
          };
          await tx.put("user-songs-map", songIdMap);
        },
        "song-modify": async (tx: WriteTransaction, song: Song) => {
          if (DEBUG) console.debug(LOG_TAG, "mutator songModify", song);
          await tx.put(`song/${song.id}`, song);
        },
        "set-list-add": async (
          tx: WriteTransaction,
          { setList, ts }: { setList: SetList; ts: Timestamp },
        ) => {
          if (DEBUG)
            console.debug(LOG_TAG, "mutator set-list-add", { setList, ts });
          const setListsPrev =
            ((await tx.get("user-set-lists")) as SetLists) || {};
          const setLists = {
            ...setListsPrev,
            [setList.id]: setList,
          };
          await tx.put("user-set-lists", setLists);
        },
        "set-list-modify": async (
          tx: WriteTransaction,
          { setList, ts }: { setList: SetList; ts: Timestamp },
        ) => {
          console.debug(LOG_TAG, "mutator set-list-modify", setList);
          const setListsPrev =
            ((await tx.get("user-set-lists")) as SetLists) || {};
          const setLists = {
            ...setListsPrev,
            [setList.id]: {
              ...setList,
              updated_timestamp: ts,
            },
          };
          await tx.put("user-set-lists", setLists);
        },
        "set-list-remove": async (
          tx: WriteTransaction,
          { setList, ts }: { setList: SetList; ts: Timestamp },
        ) => {
          console.debug(LOG_TAG, "mutator set-list-remove", setList);
          const setListsPrev =
            ((await tx.get("user-set-lists")) as SetLists) || {};
          const setLists = { ...setListsPrev };
          delete setLists[setList.id];
          await tx.put("user-set-lists", setLists);
        },

        "friend-add": async (
          tx: WriteTransaction,
          { friend, ts }: { friend: User; ts: Timestamp },
        ) => {
          console.debug(LOG_TAG, "mutator friendAdd", { friend, ts });
          const friendsPrev = ((await tx.get("friends")) as Friends) || {};
          const friends = { ...friendsPrev, [friend.id]: friend };
          await tx.put("friends", friends);

          const friendIdMapPrev =
            ((await tx.get("user-friend-map")) as FriendIdMap) || {};
          const friendIdMap = {
            ...friendIdMapPrev,
            [friend.id]: {
              added_timestamp: ts,
              updated_timestamp: ts,
              deleted_timestamp: 0,
              view_count: friendIdMapPrev[friend.id]
                ? friendIdMapPrev[friend.id]?.view_count || 0
                : 0,
              last_viewed_timestamp: ts,
            },
          };
          await tx.put("user-friend-map", friendIdMap);
          if (DEBUG)
            console.debug(LOG_TAG, "mutator friendAdd COMPLETE", {
              friendIdMap,
            });
        },
        "friend-remove": async (
          tx: WriteTransaction,
          { friendId, ts }: { friendId: UserId; ts: Timestamp },
        ) => {
          if (DEBUG)
            console.debug(LOG_TAG, "mutator friendRemove", { friendId, ts });
          const friendIdMapPrev =
            ((await tx.get("user-friend-map")) as FriendIdMap) || {};
          const friendIdMap = {
            ...friendIdMapPrev,
            [friendId]: {
              ...friendIdMapPrev[friendId],
              deleted_timestamp: ts,
            },
          };
          await tx.put("user-friend-map", friendIdMap);
        },
      },
    });

    replicache.getAuth = undefined;

    replicache.onSync = async (syncing: boolean) => {
      if (!syncing) {
        if (DEBUG)
          console.debug(LOG_TAG, "onSync complete", {
            syncing,
            replicache,
          });
      }
    };

    if (DEBUG)
      console.debug(LOG_TAG, "constructed", {
        replicache,
      });
    setReplicache(replicache);
  }, [setReplicache]);

  useEffect(() => {
    if (replicache) return;
    if (!user) {
      if (DEBUG) console.debug(LOG_TAG, "- user useEffect no user yet...");
      return;
    }

    if (DEBUG)
      console.debug(LOG_TAG, "CONSTRUCTOR useEffect", {
        user,
        replicache,
      });
    // clearDefaults
    useReplicacheStore.setState({ songIdMap: {}, songs: {} });

    setupReplicache();
  }, [replicache, user, setupReplicache]);

  const handleVisibilityChange = () => {
    if (!replicache) return;
    const newPullInterval = getPullInterval();
    if (DEBUG)
      console.debug(LOG_TAG, "handleVisibilityChange", {
        documentHidden: document.hidden,
        newPullInterval,
      });
    setPullInterval(replicache, newPullInterval, !IS_DEV);
  };

  useEventListener(
    "visibilitychange",
    handleVisibilityChange,
    typeof window !== "undefined" ? window.document : undefined,
  );

  const handleNetworkOnlineChange = () => {
    if (!replicache) return;
    console.debug(LOG_TAG, "handleNetworkOnlineChange", {
      online: navigator.onLine,
    });
    const pullInterval = navigator.onLine ? getPullInterval() : null;
    setPullInterval(replicache, pullInterval, true);
  };
  useEventListener(
    "online",
    handleNetworkOnlineChange,
    typeof window !== "undefined" ? window.document : undefined,
  );
  useEventListener(
    "offline",
    handleNetworkOnlineChange,
    typeof window !== "undefined" ? window.document : undefined,
  );
};

const songIdMapQuery = async (tx: ReadTransaction) => {
  const userSongsMap = await tx.get("user-songs-map");
  // Initially, on an empty-start, userSongsMap will always be `undefined`
  // but subsequently when we've read from the network, db if we find `{}` that will
  // legit be a new user with nothing in their songIdMap.
  if (DEBUG) console.debug(LOG_TAG, "songIdMapQuery", { userSongsMap });
  if (userSongsMap !== undefined && isEmpty(userSongsMap)) {
    if (DEBUG)
      console.debug(LOG_TAG, "songIdMapQuery FIRST TIMER", { userSongsMap });
    useReplicacheStore.setState({ isFirstTimeUser: true });
  }
  const songIdMap = userSongsMap || {};
  return songIdMap as SongIdMap;
};

export const useReplicacheSongIdMap = () => {
  const { session } = useContext(ServerContext);
  const { replicache, songIdMap, initializedSongIdMap } = useReplicacheStore(
    ({ replicache, songIdMap, initializedSongIdMap }) => ({
      replicache,
      songIdMap,
      initializedSongIdMap,
    }),
    shallow,
  );

  useEffect(() => {
    //console.debug(LOG_TAG, "useSubscribe useEffect", { replicache });
    if (!replicache) return;
    return replicache.subscribe(songIdMapQuery, {
      onData: (data: SongIdMap) => {
        // console.debug(LOG_TAG, "songIdMapQuery subscribe.onData", {
        //   songsIdMapLength: Object.keys(data).length,
        //   initializedSongIdMap,
        // });

        if (data && !isEqual(data, songIdMap)) {
          useReplicacheStore.setState({ songIdMap: data });
        }
        if (!initializedSongIdMap) {
          useReplicacheStore.setState({ initializedSongIdMap: true });
        }
      },
      onError: (err) => {
        if (DEBUG) console.debug(LOG_TAG, "subscribe.onError", { err });
        throw err;
      },
    });
  }, [initializedSongIdMap, replicache, songIdMap]);

  //console.debug("useReplicacheSongIdMap", { session });
  return {
    songIdMap: session ? songIdMap || {} : demoSongs.songIdMap,
  };
};

const songsQuery = async (tx: ReadTransaction) => {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const songsUnknown: any = await tx.scan({ prefix: "song/" }).toArray();
  // @ts-ignore
  const songs = songsUnknown.reduce((obj, item) => {
    // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
    return { ...obj, [item.id]: item };
  }, {});
  return songs as Songs;
};

export const useReplicacheSongs = () => {
  const { session } = useContext(ServerContext);
  const replicacheStore = useReplicacheStore(
    (state) => ({
      replicache: state.replicache,
      songs: state.songs,
    }),
    shallow,
  );

  useEffect(() => {
    if (!replicacheStore.replicache) return;
    return replicacheStore.replicache.subscribe(songsQuery, {
      onData: (data: Songs) => {
        const isUpdate = !isEqual(data, replicacheStore.songs);
        if (DEBUG)
          console.debug(LOG_TAG, "song/ subscribe.onData", {
            isUpdate,
            data,
          });
        if (data && isUpdate) {
          useReplicacheStore.setState({ songs: data });
        }
      },
      onError: (err) => {
        if (DEBUG) console.debug(LOG_TAG, "subscribe.onError", { err });
        throw err;
      },
    });
  }, [replicacheStore.replicache, replicacheStore.songs]);

  if (DEBUG)
    console.debug(LOG_TAG, "useReplicacheSongs", {
      session,
      songsLength: Object.keys(replicacheStore.songs).length,
      demoSongsLength: Object.keys(demoSongs.songs).length,
    });

  return {
    songs: session ? replicacheStore.songs || {} : demoSongs.songs,
  };
};

const setListsQuery = async (tx: ReadTransaction) => {
  const setLists = await tx.get("user-set-lists");
  return setLists as SetLists;
};

export const useReplicacheSetLists = () => {
  const { replicache, setLists } = useReplicacheStore(
    (state) => ({
      replicache: state.replicache,
      setLists: state.setLists,
    }),
    shallow,
  );

  useEffect(() => {
    if (DEBUG)
      console.debug(LOG_TAG, "useReplicacheSetLists useEffect", { replicache });
    if (!replicache) return;

    return replicache.subscribe(setListsQuery, {
      onData: (data: SetLists) => {
        if (DEBUG)
          console.debug(LOG_TAG, "user-set-lists subscribe.onData", data);
        if (data && !isEqual(data, setLists)) {
          useReplicacheStore.setState({ setLists: data });
        }
      },
      onError: (err) => {
        if (DEBUG) console.debug(LOG_TAG, "subscribe.onError", { err });
        throw err;
      },
    });
  }, [replicache, setLists]);

  if (DEBUG) console.debug(LOG_TAG, "useReplicacheSetLists", { setLists });
  return {
    setLists: setLists || {},
  };
};

const friendIdMapQuery = async (tx: ReadTransaction) => {
  const friendIdMap = (await tx.get("user-friend-map")) || {};
  return friendIdMap as FriendIdMap;
};

export const useReplicacheFriendIdMap = () => {
  const replicacheStore = useReplicacheStore(
    (state) => ({
      replicache: state.replicache,
      friendIdMap: state.friendIdMap,
      initializedFriendIdMap: state.initializedFriendIdMap,
    }),
    shallow,
  );

  useEffect(() => {
    if (DEBUG)
      console.debug(LOG_TAG, "useSubscribe useEffect", {
        replicache: replicacheStore.replicache,
      });
    if (!replicacheStore.replicache) return;
    return replicacheStore.replicache.subscribe(friendIdMapQuery, {
      onData: (data: FriendIdMap) => {
        if (data && !isEqual(data, replicacheStore.friendIdMap)) {
          useReplicacheStore.setState({ friendIdMap: data });
        }
        if (!replicacheStore.initializedFriendIdMap) {
          useReplicacheStore.setState({ initializedPreferences: true });
        }
      },
      onError: (err) => {
        if (DEBUG) console.debug(LOG_TAG, "subscribe.onError", { err });
        throw err;
      },
    });
  }, [
    replicacheStore.initializedFriendIdMap,
    replicacheStore.replicache,
    replicacheStore.friendIdMap,
  ]);

  return { friendIdMap: replicacheStore.friendIdMap || {} };
};

const friendsQuery = async (tx: ReadTransaction) => {
  const friends = (await tx.get("friends")) || {};
  return friends as Friends;
};

export const useReplicacheFriends = () => {
  const replicacheStore = useReplicacheStore(
    (state) => ({
      replicache: state.replicache,
      friends: state.friends,
      initializedFriends: state.initializedFriends,
    }),
    shallow,
  );

  useEffect(() => {
    if (DEBUG)
      console.debug(LOG_TAG, "useSubscribe useEffect", {
        replicache: replicacheStore.replicache,
      });
    if (!replicacheStore.replicache) return;
    return replicacheStore.replicache.subscribe(friendsQuery, {
      onData: (data: Friends) => {
        if (DEBUG)
          console.debug(LOG_TAG, "friends subscribe.onData", {
            friends: replicacheStore.friends,
            data,
          });
        if (data && !isEqual(data, replicacheStore.friends)) {
          useReplicacheStore.setState({ friends: data });
        }
        if (!replicacheStore.initializedFriends) {
          useReplicacheStore.setState({ initializedFriends: true });
        }
      },
      onError: (err) => {
        console.debug(LOG_TAG, "FRIENDS subscribe.onError", { err });
        throw err;
      },
    });
  }, [
    replicacheStore.friends,
    replicacheStore.initializedFriends,
    replicacheStore.replicache,
  ]);

  return { friends: replicacheStore.friends };
};

const preferencesQuery = async (tx: ReadTransaction) => {
  const p = await tx.get("user-preferences");
  return p as UserPreferences;
};

export const useReplicachePreferences = () => {
  const replicacheStore = useReplicacheStore(
    (state) => ({
      replicache: state.replicache,
      preferences: state.preferences,
      initializedPreferences: state.initializedPreferences,
    }),
    shallow,
  );

  useEffect(() => {
    if (DEBUG)
      console.debug(LOG_TAG, "useSubscribe useEffect", {
        replicache: replicacheStore.replicache,
      });
    if (!replicacheStore.replicache) return;
    return replicacheStore.replicache.subscribe(preferencesQuery, {
      onData: (data: UserPreferences) => {
        if (DEBUG)
          console.debug(LOG_TAG, "user-preferences subscription", {
            data,
            preferences: replicacheStore.preferences,
            initialized: replicacheStore.initializedPreferences,
          });
        if (data && !isEqual(data, replicacheStore.preferences)) {
          if (DEBUG)
            console.debug(LOG_TAG, "user-preferences subscription UPDATED");
          //replicacheStore.replicacheSetPreferences(data);
          useReplicacheStore.setState({ preferences: data });
        } else {
          if (DEBUG)
            console.debug(LOG_TAG, "user-preferences subscription NOT UPDATED");
        }
        if (!replicacheStore.initializedPreferences) {
          useReplicacheStore.setState({ initializedPreferences: true });
        }
      },
      onError: (err) => {
        if (DEBUG) console.debug(LOG_TAG, "subscribe.onError", { err });
        throw err;
      },
    });
  }, [replicacheStore]);

  return { preferences: replicacheStore.preferences };
};

export { useReplicache, useReplicacheStore };
